import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  HourlyFunnelData,
  PreEligibilityHourlyData,
  SalesDropApiResponse,
  getHourlyFunnelData,
  getPreEligibilityHourly,
  getSalesDrop,
} from "../services/funnelService";

export type FunnelSection = "hourly" | "preEligibility" | "salesDrop";

interface UseFunnelDataResult {
  hourlyData: HourlyFunnelData[];
  preEligibilityHourlyData: PreEligibilityHourlyData[];
  salesDropData: SalesDropApiResponse | null;
  lastUpdated: Date | null;
  loading: boolean;
  loadingSection: FunnelSection | null;
  reload: () => Promise<void>;
}

const isAbortError = (error: unknown): boolean =>
  error instanceof DOMException && error.name === "AbortError";

const isSameHourlyData = (
  prevHourly: HourlyFunnelData[],
  nextHourly: HourlyFunnelData[]
): boolean => {
  if (prevHourly.length !== nextHourly.length) {
    return false;
  }

  return prevHourly.every(
    (row, index) =>
      row.hour_range === nextHourly[index].hour_range &&
      Object.keys(row).every((key) => row[key] === nextHourly[index][key])
  );
};

const isSamePreEligibilityData = (
  prevRows: PreEligibilityHourlyData[],
  nextRows: PreEligibilityHourlyData[]
): boolean => {
  if (prevRows.length !== nextRows.length) {
    return false;
  }

  return prevRows.every(
    (row, index) =>
      row.hour_range === nextRows[index].hour_range &&
      row.total_pre_eligibility === nextRows[index].total_pre_eligibility &&
      row.pre_registration === nextRows[index].pre_registration &&
      row.post_registration === nextRows[index].post_registration &&
      row.middleware_customer_block === nextRows[index].middleware_customer_block &&
      row.eligible_count === nextRows[index].eligible_count &&
      row.not_eligible_count === nextRows[index].not_eligible_count &&
      row.eligible_percent === nextRows[index].eligible_percent &&
      row.not_eligible_percent === nextRows[index].not_eligible_percent
  );
};

const isSameSalesDropData = (
  prevData: SalesDropApiResponse | null,
  nextData: SalesDropApiResponse | null
): boolean => {
  if (!prevData && !nextData) {
    return true;
  }

  if (!prevData || !nextData) {
    return false;
  }

  return (Object.keys(prevData) as Array<keyof SalesDropApiResponse>).every(
    (key) => prevData[key] === nextData[key]
  );
};

export const useFunnelData = (): UseFunnelDataResult => {
  const [hourlyData, setHourlyData] = useState<HourlyFunnelData[]>([]);
  const [preEligibilityHourlyData, setPreEligibilityHourlyData] = useState<
    PreEligibilityHourlyData[]
  >([]);
  const [salesDropData, setSalesDropData] = useState<SalesDropApiResponse | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingSection, setLoadingSection] = useState<FunnelSection | null>("hourly");
  const abortControllerRef = useRef<AbortController | null>(null);

  // Calls each API in turn and publishes its data as soon as it arrives, so the
  // first section renders without waiting for the slower ones.
  const fetchAndStore = useCallback(async (showLoader: boolean) => {
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    const { signal } = controller;

    if (showLoader) {
      setLoading(true);
    }

    try {
      setLoadingSection("hourly");
      try {
        const hourly = await getHourlyFunnelData(signal);
        setHourlyData((previous) => (isSameHourlyData(previous, hourly) ? previous : hourly));
      } catch (error) {
        if (isAbortError(error)) throw error;
        console.error("Stage-wise hourly API failed", error);
      }

      setLoadingSection("preEligibility");
      try {
        const preEligibility = await getPreEligibilityHourly(signal);
        setPreEligibilityHourlyData((previous) =>
          isSamePreEligibilityData(previous, preEligibility) ? previous : preEligibility
        );
      } catch (error) {
        if (isAbortError(error)) throw error;
        console.error("Pre-eligibility hourly API failed", error);
      }

      setLoadingSection("salesDrop");
      try {
        const salesDrop = await getSalesDrop(signal);
        setSalesDropData((previous) =>
          isSameSalesDropData(previous, salesDrop) ? previous : salesDrop
        );
      } catch (error) {
        if (isAbortError(error)) throw error;
        console.error("Sales-drop API failed", error);
      }

      setLastUpdated(new Date());
    } catch (error) {
      if (!isAbortError(error)) {
        console.error("Failed to load funnel monitoring data", error);
      }
    } finally {
      // A newer request owns the loading state once this one is aborted.
      if (abortControllerRef.current === controller) {
        setLoadingSection(null);
        if (showLoader) {
          setLoading(false);
        }
      }
    }
  }, []);

  useEffect(() => {
    fetchAndStore(true);
  }, [fetchAndStore]);

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  // Clear current data so a refresh walks through the same progressive loading as the first load.
  const reload = useCallback(async () => {
    setHourlyData([]);
    setPreEligibilityHourlyData([]);
    setSalesDropData(null);
    await fetchAndStore(true);
  }, [fetchAndStore]);

  return useMemo(
    () => ({
      hourlyData,
      preEligibilityHourlyData,
      salesDropData,
      lastUpdated,
      loading,
      loadingSection,
      reload,
    }),
    [hourlyData, preEligibilityHourlyData, salesDropData, lastUpdated, loading, loadingSection, reload]
  );
};
